import { useState, useEffect, Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Equipment } from "../../types/equipments";
import { JobTask, JobUsedPart, StatusTransitionRequiredAction, StatusTransitionRequiredField } from "../../types/jobs";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import EquipmentCounterForm, {
  PreviousCountersTable,
  useEquipmentLastCounters,
} from "../ui/equipmentCounterForm/equipmentCounterForm.component";
import { JOB_SERVICES_ENDPOINT, JOB_ISSUES_ENDPOINT, ORDERED_PARTS_ENDPOINT } from "../../consts";
import { formatAddress, formatCounterValue } from "../../utils";

export interface JobStatusTransitionSaveDataPayload {
  task_updates: Array<{
    id: string;
    services?: Array<string | number>;
    issues?: Array<string | number>;
    description?: string;
  }>;
  counter_entries: Array<{ equipment_id: string | number; kind: string; value: string }>;
  used_part_entries?: Array<{
    job_task_id: string | number;
    ordered_part_id: string | number;
    quantity: string;
  }>;
}

interface JobStatusTransitionActionsModalProps {
  jobId: string;
  tasks: JobTask[];
  usedParts?: JobUsedPart[];
  initialRejectionReason?: string | null;
  requiredActions: StatusTransitionRequiredAction[];
  onSave: (payload: JobStatusTransitionSaveDataPayload) => Promise<boolean>;
  saving: boolean;
  onConfirm: (actionsData: Record<string, string>) => void;
  onClose: () => void;
  loading: boolean;
  fieldErrors?: Record<string, string[] | string>;
  // Status the job is being moved to - used to only warn about tasks missing
  // equipment when heading into verification, where it's easy to forget.
  desiredStatus?: string | null;
}

const getRequiredKindsForEquipment = (equipment: Equipment): Array<"BP" | "CP"> => {
  if (equipment.kind === "C") {
    return ["BP", "CP"];
  }

  return ["BP"];
};

const equipmentLabel = (equipment: Equipment | null, t: (key: string) => string) =>
  equipment?.full_name || equipment?.serial || t("No equipment");

const ObligatoryChip = ({ isRequired, t }: { isRequired: boolean; t: (key: string) => string }) => (
  <Chip
    size="small"
    label={isRequired ? t("Obligatory") : t("Optional")}
    color={isRequired ? "warning" : "default"}
    variant="outlined"
    sx={{ ml: 1 }}
  />
);

const parseCounterValue = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const COUNTER_KIND_LABELS: Record<string, string> = {
  BP: "Black pages",
  CP: "Color pages",
  SP: "Scanned pages",
};

interface EquipmentCounterFieldsProps {
  equipment: Equipment;
  assignedCounters: JobTask["counters"];
  jobId: string;
  onChange: (kind: string, data: Record<string, unknown>) => void;
}

const EquipmentCounterFields = ({ equipment, assignedCounters, jobId, onChange }: EquipmentCounterFieldsProps) => {
  const { t } = useTranslation();
  // A task can have several counters assigned (one per kind). If it has any
  // at all, those are the source of truth for the kinds they cover and the
  // "previous counters" reference table is hidden.
  const hasAssignedCounter = assignedCounters.length > 0;
  const lastCounters = useEquipmentLastCounters(equipment.id);
  const requiredKinds = getRequiredKindsForEquipment(equipment);
  const [valuesByKind, setValuesByKind] = useState<Record<string, unknown>>({});
  const [showScannedPages, setShowScannedPages] = useState(false);

  const handleChange = (kind: string, data: Record<string, unknown>, includeInSum: boolean) => {
    if (includeInSum) {
      setValuesByKind((prev) => ({ ...prev, [kind]: data.value }));
    }
    onChange(kind, data);
  };

  const handleHideScannedPages = () => {
    setShowScannedPages(false);
    onChange("SP", { equipment_id: equipment.id, kind: "SP", value: "" });
  };

  const parsedValues = requiredKinds.map((kind) => parseCounterValue(valuesByKind[kind]));
  const sum = parsedValues.reduce<number | null>(
    (total, value) => (value === null ? total : (total ?? 0) + value),
    null
  );

  const renderKindRow = (kind: string, includeInSum: boolean, onRemove?: () => void) => {
    // If this task already has an assigned counter of this kind, that's the
    // value to show/edit. Otherwise, prefill from the equipment's last known
    // reading only when the task has no assigned counter at all yet - once one
    // exists, it's the source of truth and stale global hints are misleading.
    const assignedMatch = assignedCounters.find((counter) => counter.kind === kind);
    const last = assignedMatch
      ? { value: Number(assignedMatch.value) }
      : !hasAssignedCounter
      ? lastCounters.find((counter) => counter.kind === kind)
      : undefined;
    return (
      <Box key={kind} sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {t(COUNTER_KIND_LABELS[kind] ?? kind)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <EquipmentCounterForm
              key={`${kind}-${last ? "loaded" : "pending"}`}
              equipmentId={equipment.id}
              jobId={jobId}
              hiddenFields={["job", "equipment_id", "kind", "provided_by_client", "read_at"]}
              hideSubmit
              hidePreviousCountersTable
              hideLastValueHint
              wideFields
              initialData={{ kind, value: last ? String(last.value) : "" }}
              onFormDataChange={(data) => handleChange(kind, data, includeInSum)}
            />
          </Box>
          {onRemove && (
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!hasAssignedCounter && <PreviousCountersTable equipmentId={equipment.id} />}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {requiredKinds.map((kind) => renderKindRow(kind, true))}
      </Box>
      {requiredKinds.length > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: "action.hover",
            alignSelf: "flex-start",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {t("Total pages")}: {sum === null ? "—" : formatCounterValue(sum)}
          </Typography>
        </Box>
      )}
      {showScannedPages ? (
        renderKindRow("SP", false, handleHideScannedPages)
      ) : (
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon />}
          onClick={() => setShowScannedPages(true)}
          sx={{ alignSelf: "flex-start" }}
        >
          {t("Add scanned pages")}
        </Button>
      )}
    </Box>
  );
};

type TaskSubFieldKey = "services" | "issues" | "counters" | "description" | "usedParts";

const JobStatusTransitionActionsModal = ({
  jobId,
  tasks,
  usedParts = [],
  initialRejectionReason,
  requiredActions,
  onSave,
  saving,
  onConfirm,
  onClose,
  loading,
  fieldErrors,
  desiredStatus,
}: JobStatusTransitionActionsModalProps) => {
  const { t } = useTranslation();

  // Tasks without equipment are silently excluded from the backend's own
  // counter/equipment validation for this transition, so nothing stops a job
  // from reaching verification with an equipment-less task - surface it here
  // instead, since this is the last stop before that happens.
  const tasksMissingEquipment = tasks.filter((task) => !task.equipment);
  const showMissingEquipmentWarning =
    desiredStatus === "FV" && tasksMissingEquipment.length > 0;

  const requiredFields = requiredActions[0]?.required_fields ?? [];
  const servicesField = requiredFields.find((f) => f.field_type === "S");
  const issuesField = requiredFields.find((f) => f.field_type === "I");
  const countersField = requiredFields.find((f) => f.field_type === "V");
  const usedPartsField = requiredFields.find((f) => f.field_type === "U");
  const descriptionField = requiredFields.find((f) => f.field_type === "T");
  const rejectionReasonField = requiredFields.find((f) => f.field_type === "R");

  // Sub-fields rendered inside each task card, obligatory ones shown first.
  // Used parts are tied to a specific task (it may represent external work
  // with no equipment) rather than the job as a whole, so they render here
  // too instead of as one job-wide block.
  const taskSubFields = (
    [
      servicesField ? { key: "services" as TaskSubFieldKey, field: servicesField } : null,
      issuesField ? { key: "issues" as TaskSubFieldKey, field: issuesField } : null,
      countersField ? { key: "counters" as TaskSubFieldKey, field: countersField } : null,
      descriptionField ? { key: "description" as TaskSubFieldKey, field: descriptionField } : null,
      usedPartsField ? { key: "usedParts" as TaskSubFieldKey, field: usedPartsField } : null,
    ].filter(Boolean) as Array<{ key: TaskSubFieldKey; field: StatusTransitionRequiredField }>
  ).sort((a, b) => Number(b.field.is_required) - Number(a.field.is_required));

  const showTaskBoxes = taskSubFields.length > 0;

  const [taskServices, setTaskServices] = useState<Record<string, OptionRecord[]>>({});
  const [taskIssues, setTaskIssues] = useState<Record<string, OptionRecord[]>>({});
  const [taskDescriptions, setTaskDescriptions] = useState<Record<string, string>>({});
  const [counterFormData, setCounterFormData] = useState<Record<string, Record<string, unknown>>>({});
  const [usedPartRowsByTask, setUsedPartRowsByTask] = useState<
    Record<string, Array<{ ordered_part: OptionRecord | null; quantity: string }>>
  >({});
  const [rejectionReason, setRejectionReason] = useState<string>(initialRejectionReason ?? "");

  useEffect(() => {
    setRejectionReason(initialRejectionReason ?? "");
  }, [initialRejectionReason]);

  useEffect(() => {
    setTaskServices(Object.fromEntries(tasks.map((task) => [task.id, task.services ?? []])));
    setTaskIssues(Object.fromEntries(tasks.map((task) => [task.id, task.issues ?? []])));
    setTaskDescriptions(Object.fromEntries(tasks.map((task) => [task.id, task.description ?? ""])));
  }, [tasks]);

  // Pre-fill with the job's already-assigned ordered parts, grouped by the
  // task they were used on, so reopening the form (e.g. after a "save
  // progress" click) shows what was actually saved instead of an empty list.
  useEffect(() => {
    const grouped: Record<string, Array<{ ordered_part: OptionRecord | null; quantity: string }>> = {};
    usedParts.forEach((usedPart) => {
      const taskId = String(usedPart.job_task_id);
      if (!grouped[taskId]) {
        grouped[taskId] = [];
      }
      grouped[taskId].push({
        ordered_part: usedPart.ordered_part as OptionRecord,
        quantity: String(usedPart.quantity),
      });
    });
    setUsedPartRowsByTask(grouped);
  }, [usedParts]);

  const getFieldErrorMessage = (fieldId: number) => {
    const rawError = fieldErrors?.[String(fieldId)];
    if (!rawError) {
      return null;
    }

    if (Array.isArray(rawError)) {
      return rawError.join(" ");
    }

    return rawError;
  };

  const handleDescriptionChange = (taskId: string, value: string) => {
    setTaskDescriptions((prev) => ({ ...prev, [taskId]: value }));
  };

  const handleCounterChange = (equipmentId: string | number, kind: string, data: Record<string, unknown>) => {
    setCounterFormData((prev) => ({ ...prev, [`${equipmentId}:${kind}`]: data }));
  };

  const addUsedPartRow = (taskId: string) =>
    setUsedPartRowsByTask((prev) => ({
      ...prev,
      [taskId]: [...(prev[taskId] ?? []), { ordered_part: null, quantity: "" }],
    }));

  const removeUsedPartRow = (taskId: string, index: number) =>
    setUsedPartRowsByTask((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] ?? []).filter((_, i) => i !== index),
    }));

  const updateUsedPartRow = (
    taskId: string,
    index: number,
    patch: Partial<{ ordered_part: OptionRecord | null; quantity: string }>
  ) =>
    setUsedPartRowsByTask((prev) => ({
      ...prev,
      [taskId]: (prev[taskId] ?? []).map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  // The value a task's equipment already has assigned for this counter kind,
  // if any - used to tell "the tech actually changed this" apart from "this
  // is just the prefilled value re-submitted unchanged", so we don't send
  // (and the backend doesn't bump read_at for) counters nobody touched.
  const getAssignedCounterValue = (equipmentId: string | number, kind: string): string | undefined => {
    const task = tasks.find((t) => t.equipment && String(t.equipment.id) === String(equipmentId));
    return task?.counters.find((counter) => counter.kind === kind)?.value;
  };

  const buildSavePayload = (): JobStatusTransitionSaveDataPayload => {
    const task_updates =
      servicesField || issuesField || descriptionField
        ? tasks.map((task) => ({
            id: task.id,
            ...(servicesField ? { services: (taskServices[task.id] ?? []).map((o) => o.id) } : {}),
            ...(issuesField ? { issues: (taskIssues[task.id] ?? []).map((o) => o.id) } : {}),
            ...(descriptionField ? { description: taskDescriptions[task.id] ?? "" } : {}),
          }))
        : [];

    const counter_entries = countersField
      ? Object.values(counterFormData)
          .filter(
            (data) =>
              data.equipment_id != null && data.kind && String(data.value ?? "").trim() !== ""
          )
          .filter((data) => {
            const assignedValue = getAssignedCounterValue(
              data.equipment_id as string | number,
              String(data.kind)
            );
            if (assignedValue === undefined) {
              return true;
            }

            const parsedNew = parseCounterValue(data.value);
            const parsedAssigned = parseCounterValue(assignedValue);
            return parsedNew === null || parsedAssigned === null || parsedNew !== parsedAssigned;
          })
          .map((data) => ({
            equipment_id: data.equipment_id as string | number,
            kind: String(data.kind),
            value: String(data.value),
          }))
      : [];

    // `undefined` (rather than []) when this field isn't shown for the current
    // transition, so the backend can tell "not part of this request" apart from
    // "the tech removed every row" - only the latter should delete used parts.
    const used_part_entries = usedPartsField
      ? tasks.flatMap((task) =>
          (usedPartRowsByTask[task.id] ?? [])
            .filter((row) => row.ordered_part && String(row.quantity).trim() !== "")
            .map((row) => ({
              job_task_id: task.id,
              ordered_part_id: (row.ordered_part as OptionRecord).id,
              quantity: String(row.quantity),
            }))
        )
      : undefined;

    return { task_updates, counter_entries, used_part_entries };
  };

  const handleSave = async () => {
    await onSave(buildSavePayload());
  };

  const handleConfirmClick = async () => {
    const saved = await onSave(buildSavePayload());
    if (!saved) {
      return;
    }

    const actionsData: Record<string, string> = {};
    if (rejectionReasonField) {
      actionsData[String(rejectionReasonField.id)] = rejectionReason;
    }

    onConfirm(actionsData);
  };

  const renderTaskSubField = (task: JobTask, key: TaskSubFieldKey, field: StatusTransitionRequiredField) => {
    if (key === "services") {
      return (
        <Box key="services" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Services")}</Typography>
            <ObligatoryChip isRequired={field.is_required} t={t} />
          </Box>
          <CustomAutocomplete
            multiple
            apiConfig={{ url: JOB_SERVICES_ENDPOINT }}
            value={taskServices[task.id] ?? []}
            onChange={(_, newValue) =>
              setTaskServices((prev) => ({ ...prev, [task.id]: newValue as OptionRecord[] }))
            }
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={t("Services")} size="small" />}
            disabled={loading || saving}
          />
          {getFieldErrorMessage(field.id) && (
            <FormHelperText error>{getFieldErrorMessage(field.id)}</FormHelperText>
          )}
        </Box>
      );
    }

    if (key === "issues") {
      return (
        <Box key="issues" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Issues")}</Typography>
            <ObligatoryChip isRequired={field.is_required} t={t} />
          </Box>
          <CustomAutocomplete
            multiple
            apiConfig={{ url: JOB_ISSUES_ENDPOINT }}
            value={taskIssues[task.id] ?? []}
            onChange={(_, newValue) =>
              setTaskIssues((prev) => ({ ...prev, [task.id]: newValue as OptionRecord[] }))
            }
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={t("Issues")} size="small" />}
            disabled={loading || saving}
          />
          {getFieldErrorMessage(field.id) && (
            <FormHelperText error>{getFieldErrorMessage(field.id)}</FormHelperText>
          )}
        </Box>
      );
    }

    if (key === "description") {
      return (
        <Box key="description" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Description")}</Typography>
            <ObligatoryChip isRequired={field.is_required} t={t} />
          </Box>
          <TextField
            value={taskDescriptions[task.id] ?? ""}
            onChange={(event) => handleDescriptionChange(task.id, event.target.value)}
            disabled={loading || saving}
            multiline
            minRows={2}
            fullWidth
          />
          {getFieldErrorMessage(field.id) && (
            <FormHelperText error>{getFieldErrorMessage(field.id)}</FormHelperText>
          )}
        </Box>
      );
    }

    if (key === "usedParts") {
      const rows = usedPartRowsByTask[task.id] ?? [];
      return (
        <Box key="usedParts" sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="body2">{t("Used parts")}</Typography>
            <ObligatoryChip isRequired={field.is_required} t={t} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {rows.map((row, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ flex: 2 }}>
                  <CustomAutocomplete
                    apiConfig={{ url: ORDERED_PARTS_ENDPOINT, queryParams: { status: "O,D" } }}
                    value={row.ordered_part}
                    onChange={(_, newValue) =>
                      updateUsedPartRow(task.id, index, { ordered_part: newValue as OptionRecord | null })
                    }
                    getOptionLabel={(option) => option.display_name || option.number}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label={t("Ordered part")} size="small" />}
                    disabled={loading || saving}
                  />
                </Box>
                <TextField
                  size="small"
                  label={t("Quantity")}
                  value={row.quantity}
                  onChange={(event) => updateUsedPartRow(task.id, index, { quantity: event.target.value })}
                  disabled={loading || saving}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ flex: 1 }}
                />
                <IconButton onClick={() => removeUsedPartRow(task.id, index)} color="error" disabled={loading || saving}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={() => addUsedPartRow(task.id)}
              variant="outlined"
              size="small"
              sx={{ alignSelf: "flex-start" }}
              disabled={loading || saving}
            >
              {t("Add used part")}
            </Button>
          </Box>
          {getFieldErrorMessage(field.id) && (
            <FormHelperText error>{getFieldErrorMessage(field.id)}</FormHelperText>
          )}
        </Box>
      );
    }

    if (!task.equipment) {
      return null;
    }

    return (
      <Box key="counters" sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <Typography variant="body2">{t("Counter values")}</Typography>
          <ObligatoryChip isRequired={field.is_required} t={t} />
        </Box>
        <EquipmentCounterFields
          equipment={task.equipment}
          assignedCounters={task.counters}
          jobId={jobId}
          onChange={(kind, data) => handleCounterChange(task.equipment!.id, kind, data)}
        />
        {getFieldErrorMessage(field.id) && (
          <FormHelperText error>{getFieldErrorMessage(field.id)}</FormHelperText>
        )}
      </Box>
    );
  };

  type Block = { key: string; isRequired: boolean; node: ReactNode };
  const blocks: Block[] = [];

  if (showTaskBoxes) {
    blocks.push({
      key: "tasks",
      isRequired: taskSubFields.some(({ field }) => field.is_required),
      node: tasks.map((task) => (
        <Box
          key={task.id}
          component="fieldset"
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, pt: 1.5 }}
        >
          <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 0.5 }}>
            {equipmentLabel(task.equipment, t)}
          </Typography>
          {task.equipment && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {[task.equipment.model, task.equipment.serial].filter(Boolean).join(" · ")}
              {formatAddress(task.equipment.installation_address)
                ? ` — ${formatAddress(task.equipment.installation_address)}`
                : ""}
            </Typography>
          )}
          {taskSubFields.map(({ key, field }) => renderTaskSubField(task, key, field))}
        </Box>
      )),
    });
  }

  if (rejectionReasonField) {
    blocks.push({
      key: "rejectionReason",
      isRequired: rejectionReasonField.is_required,
      node: (
        <Box component="fieldset" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, pt: 1.5 }}>
          <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 0.5, display: "flex", alignItems: "center" }}>
            {rejectionReasonField.checkbox_label || t("Rejection reason")}
            <ObligatoryChip isRequired={rejectionReasonField.is_required} t={t} />
          </Typography>
          <TextField
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
            disabled={loading || saving}
            multiline
            minRows={2}
            fullWidth
          />
          {getFieldErrorMessage(rejectionReasonField.id) && (
            <FormHelperText error>{getFieldErrorMessage(rejectionReasonField.id)}</FormHelperText>
          )}
        </Box>
      ),
    });
  }

  const orderedBlocks = [...blocks].sort((a, b) => Number(b.isRequired) - Number(a.isRequired));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 0.5 }}>
      {showMissingEquipmentWarning && (
        <Alert severity="warning">
          {t(
            "This job has {{count}} task(s) without equipment assigned. Make sure this is intentional before moving to verification.",
            { count: tasksMissingEquipment.length }
          )}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        {t("Fill in the fields below, then save your progress or confirm to change the job status.")}
      </Typography>

      {orderedBlocks.map((block) => (
        <Fragment key={block.key}>{block.node}</Fragment>
      ))}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          pt: 2,
          mt: 0.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button variant="outlined" onClick={onClose} disabled={loading || saving}>
          {t("Cancel")}
        </Button>
        <Button variant="outlined" onClick={handleSave} disabled={loading || saving}>
          {saving ? t("Saving...") : t("Save")}
        </Button>
        <Button variant="contained" onClick={handleConfirmClick} disabled={loading || saving}>
          {loading ? t("Processing...") : t("Accept all & change status")}
        </Button>
      </Box>
    </Box>
  );
};

export default JobStatusTransitionActionsModal;
